import React, { useMemo } from "react";
import type { ForwardRefExoticComponent } from "react";

import type {
  PolymorphicComponentPropWithRef,
  PolymorphicRef,
} from "../../polymorphic-types";

import styles from "./Button.module.css";

export type ButtonOwnProps = {
  children: React.ReactNode;
  variant?: "primary" | "secondary" | "tertiary";
  size?: "default" | "small" | "large";
  outline?: boolean;
  fullWidth?: boolean;
  loading?: boolean;
  disabled?: boolean;
};

export type ButtonProps<C extends React.ElementType = "button"> =
  PolymorphicComponentPropWithRef<C, ButtonOwnProps>;

interface ButtonComponent<C extends React.ElementType = "button">
  extends ForwardRefExoticComponent<ButtonProps<C>> {
  <C extends React.ElementType = "button">(
    /* eslint-disable-next-line no-unused-vars */
    props: ButtonProps<C>
  ): React.ReactNode;
}

function useStyleVariants({
  variant,
  size,
  outline,
  fullWidth,
  loading,
  disabled,
  className,
}: ButtonOwnProps & { className?: string }) {
  return useMemo(() => {
    const classNames = [styles.Button];
    if (variant && variant !== "primary") {
      classNames.push(styles[`Button--${variant}`]);
    }
    if (fullWidth) {
      classNames.push(styles["Button--wide"]);
    }
    if (size && size !== "default") {
      classNames.push(styles[`Button--${size}`]);
    }
    if (outline) {
      classNames.push(styles[`Button--outline`]);
    }
    if (loading) {
      classNames.push(styles[`Button--is-loading`]);
    }
    if (disabled) {
      classNames.push(styles[`Button--disabled`]);
    }
    if (className) {
      classNames.push(className);
    }
    return classNames.join(" ");
  }, [variant, fullWidth, size, outline, loading, disabled, className]);
}

export const Button: ButtonComponent = React.forwardRef(
  <C extends React.ElementType = "button">(
    {
      children,
      as,
      variant,
      size,
      outline = false,
      fullWidth = false,
      disabled = false,
      loading,
      className,
      ...props
    }: ButtonProps<C>,
    ref?: PolymorphicRef<C>
  ) => {
    const classNames = useStyleVariants({
      children,
      variant,
      size,
      outline,
      fullWidth,
      loading,
      disabled,
      className,
    });
    const Component = as || "button";
    return (
      <Component className={classNames} {...props} ref={ref}>
        <span className={styles["Button-text"]}>{children}</span>
        {typeof loading !== "undefined" && (
          <span className={styles["Button-spinner"]}></span>
        )}
      </Component>
    );
  }
);

Button.displayName = "Button";
